import { initializeDateSelectors, generateCalendar } from "./components/calendar.js";
import { fetchBookedTimes, populateDropdown, validateAndRoundTime, updateDuration } from "./components/timeDropdown.js";
import { navigateSteps } from "./components/navigation.js";
import Swal from 'sweetalert2';

// Create a custom Swal instance using a mixin for consistent styling
const customSwal = Swal.mixin({
  customClass: {
    popup: 'swal-popup',
    title: 'swal-title',
    content: 'swal-content',
    confirmButton: 'swal-confirm-button'
  },
  buttonsStyling: false
});

// Helper function to show alerts
const showAlert = (icon, title, text) => {
  customSwal.fire({ icon, title, text });
};

// Helper function to reset form fields and UI
const resetForm = () => {
  document.getElementById("booking-form").reset();
  document.getElementById("duration-text").textContent = "0 hours";
};

document.addEventListener("DOMContentLoaded", async () => {
  // DOM Elements
  const reserveButton = document.getElementById("reserve");
  const nextStepButton = document.getElementById("next-step");
  const initialStep = document.getElementById("initial");
  const step1 = document.getElementById("step-1");
  const step2 = document.getElementById("step-2");

  const monthSelect = document.getElementById("month");
  const yearSelect = document.getElementById("year");
  const calendarGrid = document.getElementById("calendar-grid");

  const fromTimeInput = document.getElementById("from-time");
  const toTimeInput = document.getElementById("to-time");
  const durationText = document.getElementById("duration-text");

  const form = document.getElementById("booking-form");

  const userIdInput = document.getElementById("user_id");
  const emailInput = document.getElementById("email");
  const nameInput = document.getElementById("name");

  // New fields
  const subjectInput = document.getElementById("subject");
  const participantsInput = document.getElementById("participants");

  let selectedDate = null;
  let startDatetime = null;
  let endDatetime = null;
  let duration = null;

  // Cache for booked times per date
  const bookedTimesCache = {};

  // Auto-populate the name field from the email
  if (emailInput && nameInput) {
    const emailValue = emailInput.value;
    const atIndex = emailValue.indexOf("@");
    nameInput.value = atIndex !== -1 ? emailValue.substring(0, atIndex) : emailValue;
  }

  // Initialize calendar selectors
  initializeDateSelectors(monthSelect, yearSelect, calendarGrid, generateCalendar);

  // Navigation Handlers
  reserveButton.addEventListener("click", () => {
    if (!selectedDate) {
      showAlert('warning', 'No Date Selected', 'Please select a date before proceeding.');
      return;
    }
    navigateSteps(initialStep, step1);
  });

  nextStepButton.addEventListener("click", () => {
    if (!fromTimeInput.value || !toTimeInput.value) {
      showAlert('warning', 'Incomplete Time Selection', 'Please select both start and end times before proceeding.');
      return;
    }
    navigateSteps(step1, step2);
  });

  // Handle date selection event
  document.addEventListener("dateSelected", async (event) => {
    const { day, month, year } = event.detail;
    selectedDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    console.log("Selected Date:", selectedDate);

    // Use cache to avoid redundant API calls
    let bookedTimes = bookedTimesCache[selectedDate];
    if (!bookedTimes) {
      try {
        bookedTimes = await fetchBookedTimes(selectedDate);
        bookedTimesCache[selectedDate] = bookedTimes;
      } catch (error) {
        console.error("Failed to fetch booked times:", error);
        showAlert('error', 'Error', 'Failed to load booked times. Please try again.');
        return;
      }
    }

    // Populate 'from' dropdown
    populateDropdown('from', fromTimeInput, "09:00", "17:30", 30, bookedTimes);
    // Clear and disable 'to' dropdown until a 'from' time is selected
    toTimeInput.innerHTML = "";
    toTimeInput.disabled = true;
  });

  // Update 'to' dropdown when 'from' time changes
  fromTimeInput.addEventListener("change", async (event) => {
    const selectedFromTime = event.target.value;
    const bookedTimes = bookedTimesCache[selectedDate] || await fetchBookedTimes(selectedDate);
    populateDropdown('to', toTimeInput, selectedFromTime, "18:00", 30, bookedTimes, selectedFromTime);
    toTimeInput.disabled = false;
  });

  // Update duration when 'to' time changes
  toTimeInput.addEventListener("change", (event) => {
    validateAndRoundTime(event, fromTimeInput, toTimeInput, durationText, (fromInput, toInput, durationDisplay) => {
      const result = updateDuration(fromInput, toInput, durationDisplay);
      startDatetime = result.startDatetime;
      endDatetime = result.endDatetime;
      duration = result.duration;
      return result;
    });
  });

  // Listen for custom time updates
  document.addEventListener("timeUpdated", async (event) => {
    const { startDatetime: start, endDatetime: end, duration: dur } = event.detail;
    startDatetime = start;
    endDatetime = end;
    duration = dur;
    await populateRoomStatus();
  });

  // Populate room availability based on selected date and time range
  const roomDropdown = document.getElementById('room');
  const populateRoomStatus = async () => {
    try {
      roomDropdown.innerHTML = '<option value="" disabled selected>Loading room availability...</option>';
      const response = await fetch(`/wp-json/booking/v1/room-status/${selectedDate}?from_time=${startDatetime}&to_time=${endDatetime}`);
      const data = await response.json();
      if (response.ok && Array.isArray(data.data)) {
        roomDropdown.innerHTML = '<option value="" disabled selected>Select a room</option>';
        data.data.forEach(room => {
          const option = document.createElement('option');
          option.value = room.room_id;
          option.textContent = room.available ? room.room_name : `${room.room_name} (Reserved)`;
          if (!room.available) option.disabled = true;
          roomDropdown.appendChild(option);
        });
        if (roomDropdown.children.length === 1) {
          roomDropdown.innerHTML = '<option value="" disabled>No rooms available</option>';
        }
      } else {
        console.error('Error fetching room availability:', data.message || 'Unknown error');
        roomDropdown.innerHTML = '<option value="" disabled>No rooms available</option>';
      }
    } catch (error) {
      console.error('Failed to fetch room availability:', error);
      roomDropdown.innerHTML = '<option value="" disabled>Failed to load rooms</option>';
    }
  };

  // Form submission handler
  document.getElementById("submit").addEventListener("click", async () => {
    // Validate required fields
    if (!roomDropdown.value || !selectedDate || !startDatetime || !endDatetime || !subjectInput.value || !participantsInput.value) {
      showAlert('warning', 'Missing Information', 'Please fill in all required fields.');
      return;
    }

    const payload = {
      room_id: roomDropdown.value,
      user_id: userIdInput.value,
      date: selectedDate,
      fromTime: startDatetime,
      toTime: endDatetime,
      title: subjectInput.value,
      participants: participantsInput.value
    };

    try {
      const response = await fetch("/wp-json/booking/v1/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Response:", result);

        // Build and send email payload
        const emailPayload = {
          to: emailInput.value,
          subject: "Booking Confirmation",
          body: `
            <p>Dear ${nameInput.value},</p>
            <p>Your booking has been confirmed with the following details:</p>
            <ul>
              <li><strong>Room:</strong> ${roomDropdown.options[roomDropdown.selectedIndex].text}</li>
              <li><strong>Date:</strong> ${selectedDate}</li>
              <li><strong>Time:</strong> ${startDatetime} to ${endDatetime}</li>
              <li><strong>Subject:</strong> ${subjectInput.value}</li>
              <li><strong>Participants:</strong> ${participantsInput.value}</li>
            </ul>
            <p>Thank you for choosing our services.</p>
            <p>Best regards,<br>Booking Team</p>
          `,
        };

        resetForm();

        const emailResponse = await fetch("/wp-json/email/v1/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailPayload),
        });

        if (!emailResponse.ok) {
          console.error("Failed to send email.");
        } else {
          console.log("Email sent successfully.");
        }

        customSwal.fire({
          title: 'Success!',
          text: 'Booking created successfully.',
          icon: 'success',
          confirmButtonText: 'OK'
        }).then(() => {
          window.location.href = "/";
        });
      } else {
        const error = await response.json();
        console.error("Error:", error);
        showAlert('error', 'Booking Failed', 'Failed to create booking. Please try again.');
      }
    } catch (error) {
      console.error("Error:", error);
      showAlert('error', 'Error', 'An error occurred. Please try again.');
    }
  });

  // Off-canvas panel handling
  const offCanvas = document.getElementById("offCanvas");
  const offCanvasOverlay = document.getElementById("offCanvasOverlay");
  const closeOffCanvas = document.getElementById("closeOffCanvas");
  const toggleButtons = document.querySelectorAll(".toggleOffCanvas");

  const openOffCanvas = () => {
    offCanvas.classList.remove("translate-x-full");
    offCanvasOverlay.classList.remove("hidden");
  };

  const closeOffCanvasPanel = () => {
    offCanvas.classList.add("translate-x-full");
    offCanvasOverlay.classList.add("hidden");
  };

  toggleButtons.forEach(button => button.addEventListener("click", openOffCanvas));
  closeOffCanvas.addEventListener("click", closeOffCanvasPanel);
  offCanvasOverlay.addEventListener("click", closeOffCanvasPanel);
});